import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Award, Briefcase, Building, TrendingUp, Users, Clock, Target, Sparkles } from 'lucide-react';

const AboutPage = () => {
  const partners = [
    {
      name: "CA Nikhil Kumar Khaitan",
      role: "Partner",
      specialization: "GST Audit & Compliance",
      experience: "4 Years as Chartered Accountant",
      description: "Oversees the firm's GST audit and compliance division, ensuring meticulous adherence to indirect tax regulations. His expertise in GST matters has helped numerous clients navigate complex compliance requirements.",
      image: "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NzE1Nzg1ODV8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      name: "CA Anubhav Agarwal",
      role: "Partner",
      specialization: "Tax Audit",
      experience: "5 Years as Chartered Accountant",
      description: "Leads the tax audit team with precision and dedication. His thorough understanding of tax laws and audit procedures ensures complete compliance and optimal tax planning for clients.",
      image: "https://images.unsplash.com/photo-1551135049-8a33b5883817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NzE1Nzg1ODV8MA&ixlib=rb-4.1.0&q=85"
    }
  ];

  const qualifiedCAs = [
    {
      name: "CA Shubham Agarwal",
      role: "Qualified Chartered Accountant",
      specialization: "Audit Team",
      tenure: "3 Years with Firm",
      experience: "Qualified CA for 5 Years",
      description: "A meticulous professional in the audit team, bringing fresh perspectives and technical excellence to complex audit engagements.",
      image: "https://images.unsplash.com/photo-1760346546771-a81d986459ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzE1Nzg1ODF8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      name: "CA Sagar Kumar Thakur",
      role: "Qualified Chartered Accountant",
      specialization: "Audit Team",
      tenure: "Recently Joined",
      experience: "Qualified 6 Months Ago",
      description: "Represents the new generation of professionals, bringing modern approaches and innovative thinking to traditional audit practices.",
      image: "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzE1Nzg1ODF8MA&ixlib=rb-4.1.0&q=85"
    }
  ];

  const keyMembers = [
    {
      name: "M. Ravi Krishna",
      role: "Senior Team Member",
      department: "Accounts Team",
      tenure: "20 Years",
      qualification: "B.Com Graduate",
      description: "An integral cog in the firm's machinery, Ravi Krishna's work exemplifies excellence beyond measure. His two decades of dedication and unparalleled expertise make him an invaluable asset.",
      icon: <Award className="h-6 w-6 text-amber-600" />
    },
    {
      name: "Anant Rana",
      role: "Senior Team Member",
      department: "Accounts Team",
      tenure: "10 Years",
      qualification: "B.Com Graduate",
      description: "The creative mind with intellectual prowess, Anant has been instrumental in modernizing the firm's processes. His innovative ideas have helped the firm adapt to changing times while maintaining core values.",
      icon: <Sparkles className="h-6 w-6 text-#1B5E20" />
    },
    {
      name: "Santosh Kumar",
      role: "Senior Team Member",
      department: "IT Return Filing",
      tenure: "20 Years",
      qualification: "Science Background",
      description: "Despite coming from a science background, Santosh's perseverance has established him as a master in IT return filing. Known for his error-free work and strong ethical standards.",
      icon: <Target className="h-6 w-6 text-blue-600" />
    },
    {
      name: "Vikash Rathi",
      role: "Senior Team Member",
      department: "GST Department",
      tenure: "20 Years",
      qualification: "Lawyer by Profession",
      description: "Responsible for GST return filing with legal expertise that adds depth to the firm's indirect tax services. His two decades of commitment reflect the firm's culture of excellence.",
      icon: <Building className="h-6 w-6 text-purple-600" />
    },
    {
      name: "Satyakant",
      role: "Team Member",
      department: "Concurrent Audit",
      tenure: "5 Years",
      qualification: "Banking Expert",
      description: "A specialist in concurrent audit with unmatched knowledge of banking operations. His expertise in banking matters makes him an indispensable member of the audit team.",
      icon: <TrendingUp className="h-6 w-6 text-indigo-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-900/30">
      {/* Hero Section */}
      <section className="bg-[#1B5E20] py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full">
              <span className="text-white font-medium">Our Story</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About S.K. Mundhra & Co.
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              35 years of unwavering commitment to excellence, ethics, and client success
            </p>
          </div>
        </div>
      </section>

      {/* Legacy Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
              <div>
                <h2 className="text-4xl font-bold text-slate-900 mb-6">
                  A Legacy Built on <span className="text-#0D3D13">Trust & Excellence</span>
                </h2>
                <div className="space-y-4 text-lg text-slate-700 leading-relaxed">
                  <p>
                    Established in <span className="font-semibold text-#0D3D13">1991</span>, S.K. Mundhra & Co. has grown from humble beginnings to become one of <span className="font-semibold">Odisha's most respected Chartered Accountancy firms</span>.
                  </p>
                  <p>
                    Under the visionary leadership of <span className="font-semibold text-#0D3D13">CA S.K. Mundhra</span>, the firm has built an unshakeable reputation for integrity, precision, and client-centric service delivery.
                  </p>
                  <p>
                    With a team strength of <span className="font-semibold text-#0D3D13">over 20 professionals</span> including three Chartered Accountant partners and two qualified CAs, we combine decades of experience with contemporary expertise.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzE1Nzg1ODF8MA&ixlib=rb-4.1.0&q=85"
                  alt="Team collaboration"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>

            {/* Pillars */}
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-emerald-100/50 rounded-2xl">
                <div className="w-16 h-16 bg-#1B5E20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Continuity</h3>
                <p className="text-slate-700">
                  Several team members have been with us for over 20 years, reflecting remarkable stability and trust
                </p>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl">
                <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Innovation</h3>
                <p className="text-slate-700">
                  Regular infusion of newly qualified CAs ensures we stay current with modern practices and technologies
                </p>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Excellence</h3>
                <p className="text-slate-700">
                  Every engagement is handled with meticulous attention to detail and unwavering ethical standards
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-green-900/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Our Partners
              </h2>
              <p className="text-xl text-slate-600">
                Leadership that combines expertise with dedication
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {partners.map((partner, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-green-200">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={partner.image} 
                      alt={partner.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl text-slate-900 mb-2">{partner.name}</CardTitle>
                        <Badge className="bg-#1B5E20 text-white mb-3">{partner.role}</Badge>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-#0D3D13 font-semibold">
                        <Briefcase className="h-4 w-4" />
                        {partner.specialization}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4" />
                        {partner.experience}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 leading-relaxed">{partner.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Qualified CAs Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Qualified Chartered Accountants
              </h2>
              <p className="text-xl text-slate-600">
                Infusing new energy and modern approaches
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {qualifiedCAs.map((ca, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-blue-200">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={ca.image} 
                      alt={ca.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardHeader>
                    <div>
                      <CardTitle className="text-2xl text-slate-900 mb-2">{ca.name}</CardTitle>
                      <Badge className="bg-blue-600 text-white mb-3">{ca.role}</Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-blue-700 font-semibold">
                        <Briefcase className="h-4 w-4" />
                        {ca.specialization}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4" />
                        {ca.experience} | {ca.tenure}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 leading-relaxed">{ca.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Team Members */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-#1B5E20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Key Team Members
              </h2>
              <p className="text-xl text-white">
                The backbone of our operations - dedication spanning decades
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {keyMembers.map((member, index) => (
                <Card key={index} className="bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-white/50 transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-slate-700/50 rounded-lg">
                        {member.icon}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl text-white mb-1">{member.name}</CardTitle>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className="bg-#1B5E20 text-white">{member.department}</Badge>
                          <Badge variant="outline" className="border-amber-500 text-amber-400">{member.tenure}</Badge>
                        </div>
                        <p className="text-sm text-gray-400 mb-1">{member.qualification}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 leading-relaxed">{member.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Photo Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Our Team
              </h2>
              <p className="text-lg text-slate-600">
                The dedicated professionals behind S.K. Mundhra & Co.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_ca-firm-site/artifacts/ueleqe7t_6e403a44-6cc3-493d-b3a1-e05a54afe728.JPG"
                alt="S.K. Mundhra & Co. Team"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#1B5E20]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Why Continuity & Innovation Matter
            </h2>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Our unique strength lies in the perfect balance between <span className="font-semibold text-white">experienced veterans</span> who have been with us for over two decades and <span className="font-semibold text-white">freshly qualified professionals</span> bringing modern perspectives. This ensures we never become redundant, constantly adapting to new changes while maintaining the highest standards of excellence and ethical practice.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <p className="text-2xl text-white font-light italic">
                "Excellence is not a destination but a continuous journey—one we walk together, generation after generation."
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
